import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  notification,
  Row,
  Col,
} from "antd";
import ProjectSettingToggle from "./ProjectSettingToggle";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import queryClient from "../queryClient";
import { updatePunchListItem } from "../services/punchListsAPI";
import AttachmentsSection from "./AttachmentsSection";
import ProjectUserSelect from "./ProjectUserSelect";

const UpdatePunchListItemModal = ({
  visible,
  onClose,
  punchListId,
  punchListItem,
}) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const userRole = selectedProject?.role;
  const hasFullPunchListAccess =
    userRole === "owner" ||
    userRole === "project_manager" ||
    userRole === "field";
  const [form] = Form.useForm();
  const emailNotifications = Form.useWatch("email_notifications", form) ?? true;

  const initialValues = {
    status: "Incomplete",
    email_notifications: true,
  };

  const mutation = useMutation({
    mutationFn: updatePunchListItem,
    onSuccess: () => {
      queryClient.invalidateQueries(["punch-list-items"]);
      onClose();
      form.resetFields();
      notification.success({ message: "Punch list item updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update punch list item",
        description: error.message,
      });
    },
  });

  const handleCreatePunchListItem = async (values) => {
    const punchListData = {
      ...values,
      project_id: projectId,
      attachments: values.attachments?.map((file) => file) || null,
    };
    const emailNotifications = form.getFieldValue("email_notifications") ?? true;
    mutation.mutate({
      punchListId: punchListId,
      punchListItemId: punchListItem?._id || punchListItem?.id,
      punchListItem: punchListData,
      emailNotifications,
    });
  };

  useEffect(() => {
    if (visible && punchListItem) {
      form.setFieldsValue({
        ...punchListItem,
        attachments: punchListItem.attachments || [],
        email_notifications: true,
      });
    }
  }, [visible, punchListItem, form]);

  return (
    <Modal
      title="Update Punch List Item"
      open={visible}
      onCancel={onClose}
      footer={null}
      className="w-full max-w-4xl"
    >
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={handleCreatePunchListItem}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Number"
              name="number"
              rules={[{ required: true, message: "Please input the number!" }]}
            >
              <Input disabled={!hasFullPunchListAccess} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Status" name="status">
              <Select defaultValue="Incomplete" disabled={!hasFullPunchListAccess}>
                <Select.Option value="Complete">Complete</Select.Option>
                <Select.Option value="Incomplete">Incomplete</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Please input the title!" }]}
          className="w-full"
        >
          <Input disabled={!hasFullPunchListAccess} />
        </Form.Item>
        <Form.Item
          label="Approver"
          name="approver_id"
          rules={[{ required: true, message: "Please select the approver!" }]}
          className="w-full"
        >
          <ProjectUserSelect
            mode={null}
            placeholder="Select approver"
            className="w-full"
            disabled={!hasFullPunchListAccess}
          />
        </Form.Item>
        <Form.Item
          label="Assignees"
          name="assignee_ids"
          rules={[{ required: true, message: "Please select the assignees!" }]}
          className="w-full"
        >
          <ProjectUserSelect
            placeholder="Select assignees"
            className="w-full"
            disabled={!hasFullPunchListAccess}
          />
        </Form.Item>
        <Form.Item label="Location" name="location" className="w-full">
          <Input disabled={!hasFullPunchListAccess} />
        </Form.Item>

        <Form.Item name="attachments" label="Attachments" className="w-full">
          <AttachmentsSection
            projectId={projectId}
            disabled={!hasFullPunchListAccess}
          />
        </Form.Item>
        
        <div className="project-settings-grid">
          <ProjectSettingToggle name="email_notifications" title="Email Notifications" initialValue={true} />
        </div>

        {hasFullPunchListAccess && (
          <Form.Item className="w-full">
            <Button
              type="primary"
              htmlType="submit"
              loading={mutation.isLoading}
              className="w-full mt-4"
            >
              {emailNotifications ? "Update and Send Emails" : "Update Punch List Item"}
            </Button>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default UpdatePunchListItemModal;
